/**
 * EventClubsTab
 *
 * Vista tipo "conglomerado" para el organizador (asociación) que muestra
 * cada club inscrito al evento como una tarjeta con:
 *  - Logo + nombre del club
 *  - Cantidad de inscritos / aprobados / pendientes
 *  - Progreso de check-in (consents completados)
 *  - Estado financiero (pagado vs total)
 *  - Click en una tarjeta → expande grilla de miembros con avatar + estado
 *    de consentimiento + botón para abrir el visor de documentos.
 *
 * Pensado para que la asociación tenga una "foto" rápida de cómo va cada club
 * en términos de inscripciones y de check-in, antes de comenzar el evento.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  CheckCircle2,
  Clock,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Search,
  ShieldCheck,
  AlertCircle,
  FileText,
  Image as ImageIcon,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card";
import { useEventConsents } from "@/hooks/useEventConsents";
import { useClubs } from "@/hooks/useOrganization";
import ConsentStatusBadge from "@/components/consent/ConsentStatusBadge";
import MemberConsentDocsModal from "@/components/consent/MemberConsentDocsModal";
import EventClubAttachmentsPanel from "@/components/events/EventClubAttachmentsPanel";
import { formatCurrency } from "@/lib/utils";
import type {
  Event,
  EventRegistration,
  EventConsent,
  EventPayment,
} from "@/types";

interface Props {
  event: Event;
  registrations: EventRegistration[] | undefined;
  eventPayments?: EventPayment[];
}

interface ClubAggregate {
  clubId: string;
  clubName: string;
  total: number;
  approved: number;
  pending: number;
  paid: number;
  amount: number;
  paidAmount: number;
  consentCompleted: number;
  consentInProgress: number;
  consentNotStarted: number;
  registrations: EventRegistration[];
}

export function EventClubsTab({ event, registrations, eventPayments }: Props) {
  const {
    byMemberId,
    progressFor,
    loading: loadingConsents,
  } = useEventConsents(event.id, event.consentChecklistConfig);
  const { data: allClubs } = useClubs();
  const clubLogoById = useMemo(() => {
    const map = new Map<string, string | undefined>();
    (allClubs ?? []).forEach((c) => {
      if (c.id) map.set(c.id, c.logoUrl);
    });
    return map;
  }, [allClubs]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [docsModal, setDocsModal] = useState<{
    consent: EventConsent | null;
    memberName: string;
  } | null>(null);

  const aggregates = useMemo<ClubAggregate[]>(() => {
    if (!registrations) return [];
    const map = new Map<string, ClubAggregate>();

    // Sum approved payments per club (source of truth para recaudo)
    const paidByClub = new Map<string, number>();
    (eventPayments ?? [])
      .filter((p) => p.status === "approved")
      .forEach((p) => {
        paidByClub.set(p.clubId, (paidByClub.get(p.clubId) ?? 0) + p.amount);
      });

    registrations.forEach((r) => {
      // Excluir cancelados/rechazados del conglomerado
      if (["cancelled", "rejected"].includes(r.status)) return;

      let agg = map.get(r.clubId);
      if (!agg) {
        agg = {
          clubId: r.clubId,
          clubName: r.clubName,
          total: 0,
          approved: 0,
          pending: 0,
          paid: 0,
          amount: 0,
          paidAmount: paidByClub.get(r.clubId) ?? 0,
          consentCompleted: 0,
          consentInProgress: 0,
          consentNotStarted: 0,
          registrations: [],
        };
        map.set(r.clubId, agg);
      }
      agg.registrations.push(r);
      agg.total++;
      if (r.status === "approved") agg.approved++;
      if (r.status === "pending") agg.pending++;
      if (r.paymentStatus === "paid") agg.paid++;
      // amount = total a cobrar solo de aprobados (consistente con header)
      if (r.status === "approved") agg.amount += r.unitPrice ?? 0;

      const prog = progressFor(r.memberId);
      if (prog.completed) agg.consentCompleted++;
      else if (prog.doneCount > 0) agg.consentInProgress++;
      else agg.consentNotStarted++;
    });

    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [registrations, progressFor, eventPayments]);

  const filtered = useMemo(() => {
    if (!search.trim()) return aggregates;
    const term = search.toLowerCase();
    return aggregates.filter((c) => c.clubName.toLowerCase().includes(term));
  }, [aggregates, search]);

  const totals = useMemo(() => {
    return aggregates.reduce(
      (acc, c) => ({
        clubs: acc.clubs + 1,
        members: acc.members + c.total,
        approved: acc.approved + c.approved,
        consentCompleted: acc.consentCompleted + c.consentCompleted,
        amount: acc.amount + c.amount,
        paidAmount: acc.paidAmount + c.paidAmount,
      }),
      {
        clubs: 0,
        members: 0,
        approved: 0,
        consentCompleted: 0,
        amount: 0,
        paidAmount: 0,
      },
    );
  }, [aggregates]);

  const toggleExpand = (clubId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(clubId)) next.delete(clubId);
      else next.add(clubId);
      return next;
    });
  };

  if (!registrations || registrations.length === 0) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="py-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900">
            Aún no hay clubes inscritos
          </h3>
          <p className="text-gray-500 mt-1 text-sm">
            Cuando los directores inscriban a sus miembros aparecerán aquí.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Resumen global */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <SummaryCard
          icon={<Users className="w-4 h-4" />}
          label="Clubes"
          value={totals.clubs.toString()}
          tone="purple"
        />
        <SummaryCard
          icon={<Users className="w-4 h-4" />}
          label="Inscritos"
          value={totals.members.toString()}
          tone="blue"
        />
        <SummaryCard
          icon={<CheckCircle2 className="w-4 h-4" />}
          label="Aprobados"
          value={`${totals.approved}/${totals.members}`}
          tone="green"
        />
        <SummaryCard
          icon={<ShieldCheck className="w-4 h-4" />}
          label="Check-in OK"
          value={`${totals.consentCompleted}/${totals.members}`}
          tone="emerald"
        />
        <SummaryCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Recaudado"
          value={formatCurrency(totals.paidAmount)}
          sub={`de ${formatCurrency(totals.amount)}`}
          tone="amber"
        />
      </div>

      {/* Buscador */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar club..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-4 py-2 text-sm border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all w-full"
        />
      </div>

      {loadingConsents && (
        <p className="text-xs text-gray-400">Cargando estado de check-in…</p>
      )}

      {/* Grid de tarjetas */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((club) => {
          const isOpen = expanded.has(club.clubId);
          const consentPct =
            club.total > 0
              ? Math.round((club.consentCompleted / club.total) * 100)
              : 0;
          const paidPct =
            club.amount > 0
              ? Math.round((club.paidAmount / club.amount) * 100)
              : 0;

          return (
            <Card
              key={club.clubId}
              className="border-0 shadow-md hover:shadow-lg transition-shadow overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-br from-purple-50 to-white border-b">
                <button
                  onClick={() => toggleExpand(club.clubId)}
                  className="w-full text-left p-4 hover:from-purple-100 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {clubLogoById.get(club.clubId) ? (
                        <img
                          src={clubLogoById.get(club.clubId)}
                          alt={club.clubName}
                          className="w-10 h-10 rounded-full object-cover shrink-0 border border-purple-200 bg-white"
                          loading="lazy"
                          onError={(e) => {
                            (
                              e.currentTarget as HTMLImageElement
                            ).style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 text-white flex items-center justify-center font-bold text-sm shrink-0">
                          {club.clubName
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {club.clubName}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {club.total} inscritos · {club.approved} aprobados
                          {club.pending > 0 && ` · ${club.pending} pendientes`}
                        </p>
                      </div>
                    </div>
                    {isOpen ? (
                      <ChevronUp className="w-4 h-4 text-gray-400 mt-1" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400 mt-1" />
                    )}
                  </div>
                </button>
                <div className="px-4 pb-3 -mt-1">
                  <Link
                    to={`/events/${event.id}/clubs/${club.clubId}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-900 hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Ver detalle completo
                  </Link>
                </div>
              </div>

              {/* Métricas */}
              <CardContent className="p-4 space-y-3">
                {/* Check-in progress */}
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" />
                      Check-in completo
                    </span>
                    <span className="font-medium text-gray-700">
                      {club.consentCompleted}/{club.total} ({consentPct}%)
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        consentPct >= 80
                          ? "bg-green-500"
                          : consentPct >= 40
                            ? "bg-amber-500"
                            : "bg-red-400"
                      }`}
                      style={{ width: `${consentPct}%` }}
                    />
                  </div>
                  {club.consentInProgress > 0 && (
                    <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {club.consentInProgress} en progreso
                    </p>
                  )}
                  {club.consentNotStarted > 0 && (
                    <p className="text-[11px] text-gray-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {club.consentNotStarted} sin iniciar
                    </p>
                  )}
                </div>

                {/* Pagos */}
                <div>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600 flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Pagado
                    </span>
                    <span className="font-medium text-gray-700">
                      {paidPct}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${paidPct}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {formatCurrency(club.paidAmount)} de{" "}
                    {formatCurrency(club.amount)}
                  </p>
                </div>

                {/* Detalle de miembros (expandible) */}
                {isOpen && (
                  <div className="pt-3 border-t -mx-4 -mb-4 px-4 pb-4 bg-gray-50/60 space-y-4">
                    {/* Documentos anexos del club */}
                    <div>
                      <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        Documentos anexos del club
                      </p>
                      <EventClubAttachmentsPanel
                        event={{ id: event.id, name: event.name }}
                        clubId={club.clubId}
                        clubName={club.clubName}
                        canEdit={false}
                        compact
                      />
                    </div>

                    <div>
                      <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide mb-2">
                        Miembros ({club.registrations.length})
                      </p>
                      <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                        {club.registrations.map((r) => {
                          const consent = byMemberId(r.memberId);
                          const prog = progressFor(r.memberId);
                          return (
                            <li
                              key={r.id}
                              className="flex items-center gap-2 p-2 rounded-lg bg-white border border-gray-100 hover:border-purple-200 transition-colors text-xs"
                            >
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 text-white flex items-center justify-center font-bold text-[10px] shrink-0">
                                {r.personName
                                  ?.split(" ")
                                  .map((w) => w[0])
                                  .join("")
                                  .slice(0, 2)
                                  .toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-900 truncate">
                                  {r.personName}
                                </p>
                                <p className="text-[10px] text-gray-500">
                                  {r.category}
                                </p>
                              </div>
                              <ConsentStatusBadge progress={prog} />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDocsModal({
                                    consent,
                                    memberName: r.personName,
                                  });
                                }}
                                disabled={!consent}
                                title={
                                  consent
                                    ? "Ver documentos"
                                    : "Sin documentos aún"
                                }
                                className="p-1.5 rounded-md hover:bg-purple-50 text-purple-600 disabled:opacity-30 disabled:hover:bg-transparent"
                              >
                                {consent?.pdfDocument ? (
                                  <FileText className="w-3.5 h-3.5" />
                                ) : (
                                  <ImageIcon className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filtered.length === 0 && search && (
        <Card className="border-0 shadow-md">
          <CardContent className="py-8 text-center text-sm text-gray-500">
            No se encontraron clubes con "{search}".
          </CardContent>
        </Card>
      )}

      {/* Visor de documentos */}
      {docsModal && (
        <MemberConsentDocsModal
          isOpen
          onClose={() => setDocsModal(null)}
          consent={docsModal.consent}
          memberName={docsModal.memberName}
        />
      )}
    </div>
  );
}

interface SummaryCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone: "purple" | "blue" | "green" | "emerald" | "amber";
}

function SummaryCard({ icon, label, value, sub, tone }: SummaryCardProps) {
  const tones: Record<SummaryCardProps["tone"], string> = {
    purple: "from-purple-500 to-purple-600",
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    emerald: "from-emerald-500 to-emerald-600",
    amber: "from-amber-500 to-amber-600",
  };
  return (
    <Card className="border-0 shadow-md overflow-hidden">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <span
            className={`w-6 h-6 rounded-md bg-gradient-to-br ${tones[tone]} text-white flex items-center justify-center`}
          >
            {icon}
          </span>
          {label}
        </div>
        <p className="text-lg font-bold text-gray-900 leading-tight">{value}</p>
        {sub && <p className="text-[10px] text-gray-500">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default EventClubsTab;
